import { getSupabaseClient } from './supabase';

export const ID_PROOF_TYPES = [
  'Aadhaar',
  'Passport',
  'Driving License',
  'Voter ID',
  'Other',
] as const;

export type IdProofType = (typeof ID_PROOF_TYPES)[number];

export const PURPOSE_OF_STAY_OPTIONS = [
  'Business',
  'Leisure',
  'Medical',
  'Family Function',
  'Other',
] as const;

export type PurposeOfStay = (typeof PURPOSE_OF_STAY_OPTIONS)[number];

/**
 * Mask ID proof number to show only the last 4 characters for directory list privacy.
 * E.g. "123456789012" -> "•••• •••• 9012"
 */
export function maskIdProofNumber(num?: string | null): string {
  if (!num) return '-';
  const clean = num.trim();
  if (clean.length <= 4) return clean;
  const visible = clean.slice(-4);
  const maskedCount = Math.max(4, clean.length - 4);
  const maskBullets = '•'.repeat(Math.min(maskedCount, 8));
  return `${maskBullets} ${visible}`;
}

/**
 * Retrieve a signed URL for private bucket `guest-documents`.
 * If it's already an http(s) URL, returns it directly.
 */
export async function getDocumentSignedUrl(pathOrUrl?: string | null): Promise<string | null> {
  if (!pathOrUrl) return null;
  const trimmed = pathOrUrl.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  const supabase = getSupabaseClient();
  if (!supabase) return null;

  try {
    const { data, error } = await supabase.storage
      .from('guest-documents')
      .createSignedUrl(trimmed, 3600);

    if (error) {
      console.warn('createSignedUrl for guest-documents failed:', error.message);
      // Fallback to public URL in case public read is enabled
      const { data: pub } = supabase.storage.from('guest-documents').getPublicUrl(trimmed);
      return pub?.publicUrl || null;
    }
    return data?.signedUrl || null;
  } catch (err) {
    console.warn('Exception generating signed URL for guest doc:', err);
    return null;
  }
}

/**
 * Upload guest KYC document to private bucket `guest-documents`.
 * Enforces tenant-scoped path: `<tenant_id>/room_bookings/<booking_id>/<filename>`
 */
export async function uploadGuestKYCDocument(
  tenantId: string,
  bookingId: string | null | undefined,
  file: File
): Promise<string> {
  const supabase = getSupabaseClient();
  if (!supabase) throw new Error('Database connection not available.');

  const bId = bookingId || `new-${Date.now()}`;
  const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${tenantId}/room_bookings/${bId}/${Date.now()}_${sanitizedName}`;

  const { error } = await supabase.storage
    .from('guest-documents')
    .upload(path, file, {
      cacheControl: '3600',
      upsert: true,
    });

  if (error) {
    console.error('guest-documents upload error:', error);
    throw new Error(`Failed to upload ID document: ${error.message}`);
  }

  return path;
}

export interface CustomerKYCProfile {
  id: string;
  customer_name: string;
  phone: string;
  address?: string | null;
  id_proof_type?: string | null;
  id_proof_number?: string | null;
  id_document_url?: string | null;
}

/**
 * Auto-fill lookup: search retail_customers by phone number
 */
export async function lookupCustomerByPhone(
  tenantId: string,
  phone: string
): Promise<CustomerKYCProfile | null> {
  const cleanPhone = phone.trim().replace(/\D/g, '');
  if (!cleanPhone || cleanPhone.length < 10) return null;

  const supabase = getSupabaseClient();
  if (!supabase) return null;

  try {
    const { data, error } = await supabase
      .from('retail_customers')
      .select('id, customer_name, phone, address, id_proof_type, id_proof_number, id_document_url')
      .eq('tenant_id', tenantId)
      .eq('phone', phone.trim())
      .maybeSingle();

    if (error) {
      console.warn('lookupCustomerByPhone error:', error.message);
      return null;
    }
    return data || null;
  } catch (err) {
    console.warn('lookupCustomerByPhone exception:', err);
    return null;
  }
}

export interface DeletionSafetyResult {
  canDelete: boolean;
  message?: string;
  hasInvoices?: boolean;
  hasSaleOrders?: boolean;
  hasOrders?: boolean;
  hasBookings?: boolean;
  hasPayments?: boolean;
}

/**
 * Check deletion safety rule before deleting a retail_customers row.
 * Blocks if linked to sales_invoices, sale_orders, orders, room_bookings, or customer_payments.
 */
export async function checkCustomerDeletionSafety(
  tenantId: string,
  customerId: string
): Promise<DeletionSafetyResult> {
  const supabase = getSupabaseClient();
  if (!supabase) return { canDelete: false, message: 'Database client not ready' };

  try {
    const [invResp, soResp, orderResp, bookingResp, payResp] = await Promise.all([
      supabase.from('sales_invoices').select('id').eq('customer_id', customerId).limit(1),
      supabase.from('sale_orders').select('id').eq('customer_id', customerId).limit(1),
      supabase.from('orders').select('id').eq('customer_id', customerId).limit(1),
      supabase.from('room_bookings').select('id').eq('retail_customer_id', customerId).limit(1),
      supabase.from('customer_payments').select('id').eq('customer_id', customerId).limit(1),
    ]);

    const hasInvoices = (invResp.data || []).length > 0;
    const hasSaleOrders = (soResp.data || []).length > 0;
    const hasOrders = (orderResp.data || []).length > 0;
    const hasBookings = (bookingResp.data || []).length > 0;
    const hasPayments = (payResp.data || []).length > 0;

    if (hasInvoices || hasSaleOrders || hasOrders || hasBookings || hasPayments) {
      const parts: string[] = [];
      if (hasBookings) parts.push('Room Bookings');
      if (hasOrders) parts.push('Restaurant Orders');
      if (hasInvoices || hasSaleOrders) parts.push('Retail Invoices / Orders');
      if (hasPayments) parts.push('Payments');

      return {
        canDelete: false,
        hasInvoices,
        hasSaleOrders,
        hasOrders,
        hasBookings,
        hasPayments,
        message: `Pehle is customer ke saare records clear/reassign karein. (Linked with: ${parts.join(', ')})`,
      };
    }

    return { canDelete: true };
  } catch (err: any) {
    console.error('checkCustomerDeletionSafety error:', err);
    return {
      canDelete: false,
      message: err.message || 'Pehle is customer ke saare records clear/reassign karein',
    };
  }
}
